import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSession } from '../session'
import { publicClient } from '../trpc'
import { createPkceChallenge, stashVerifier } from '../auth/pkce'
import { Capacitor } from '@capacitor/core'
import { resolveReturnTo, buildReturnUrl, type ReturnStatus } from '../auth/redirect'
import { getHostedUiDomain, getEndUserClientId } from '../config/runtime'
import { AuthLayout, AuthLoading } from '../components/AuthLayout'
import { AppleSignInButton, GoogleSignInButton, useResetOnPageRestore } from '@weapps/design'
import { useT } from '../i18n'

const RETURN_TO_KEY = 'wa_return_to'

// Email-first router (ADR 0052, applying ADR 0043). One email field drives every branch via the
// broker's `emailStatus`, so a Google-email user is routed to Google rather than creating a duplicate
// password account.
type Step = 'chooser' | 'email' | 'password' | 'create' | 'confirm' | 'google' | 'apple' | 'forgot' | 'forgotConfirm'

export function SignInPage() {
  const { login, account, isLoading } = useSession()
  const t = useT()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = resolveReturnTo(searchParams, '/profile')
  const isExternal = returnTo.startsWith('http')

  // Capacitor plugins (and the native social handoff) don't work on this cross-origin page; inside the
  // shell social sign-in lives on grow-mobile's Login Screen, so the accounts page hides social and opens
  // straight on the email step. In the browser it opens on the Sign-in Chooser (grill 2026-09-05).
  const inShell = Capacitor.isNativePlatform()
  const [step, setStep] = useState<Step>(inShell ? 'email' : 'chooser')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!isLoading && account) finish('success')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isLoading])

  // Abandoned Sign-in via the browser's Back button: `startFederated` leaves this page with `busy` set,
  // and a bfcache restore brings it back with that state intact — the full-card loader, forever. Reset
  // on the restore so the chooser is usable again.
  useResetOnPageRestore(() => setBusy(false))

  function finish(status: ReturnStatus) {
    const url = buildReturnUrl(returnTo, status)
    if (url) window.location.href = url
    else navigate(returnTo.startsWith('/') ? returnTo : '/profile', { replace: true })
  }

  async function run(fn: () => Promise<void>) {
    setError(null)
    setBusy(true)
    try {
      await fn()
    } catch (err) {
      setError(err instanceof Error ? err.message : t.somethingWrong)
    } finally {
      setBusy(false)
    }
  }

  const onEmail = () =>
    run(async () => {
      const status = await publicClient.emailStatus.query({ email })
      if (!status.exists) return setStep('create')
      if (status.status === 'ACTIVE' && status.type === 'GOOGLE') return setStep('google')
      if (status.status === 'ACTIVE' && status.type === 'APPLE') return setStep('apple')
      if (status.status === 'UNCONFIRMED') {
        setInfo(t.confirmNeeded)
        return setStep('confirm')
      }
      if (status.status === 'FORCE_CHANGE_PASSWORD') return setStep('forgot')
      return setStep('password')
    })

  const onPassword = () => run(async () => { await login(email, password); finish('success') })

  const onCreate = () =>
    run(async () => {
      await publicClient.signUp.mutate({ email, password, name })
      setInfo(t.confirmCodeSent)
      setStep('confirm')
    })

  const onConfirm = () =>
    run(async () => {
      await publicClient.confirmSignUp.mutate({ email, code })
      await login(email, password)
      finish('success')
    })

  const onResend = () => run(async () => { await publicClient.resendSignUpCode.mutate({ email }); setInfo(t.codeResent) })

  const onForgot = () =>
    run(async () => {
      await publicClient.forgotPassword.mutate({ email })
      setInfo(t.resetCodeSent)
      setStep('forgotConfirm')
    })

  const onForgotConfirm = () =>
    run(async () => {
      await publicClient.confirmForgotPassword.mutate({ email, code, newPassword: password })
      setInfo(t.passwordUpdated)
      setCode('')
      setStep('password')
    })

  // Web-only federation redirect (ADR 0052). Social sign-in isn't offered inside the native shell —
  // Capacitor plugins only work on the app.polux origin, so the shell runs its own flow on grow-mobile's
  // Login Screen (ADR 0090, #200/#228); accounts.polux hides its social buttons there (inShell gate).
  const startFederated = async (provider: 'Google' | 'SignInWithApple') => {
    setError(null)
    const domain = getHostedUiDomain()
    const clientId = getEndUserClientId()
    if (!domain || !clientId) return setError(t.googleNotConfigured)
    setBusy(true) // show the full-card loader while we build the PKCE challenge and redirect away
    try {
      const { verifier, challenge } = await createPkceChallenge()
      stashVerifier(verifier)
      sessionStorage.setItem(RETURN_TO_KEY, returnTo)
      const redirectUri = `${window.location.origin}/callback`
      const scope = provider === 'Google' ? 'openid email profile' : 'openid email'
      // prompt=select_account: always show the provider's account chooser, so "sign out → Continue with
      // Google" never silently re-signs the last account (shared devices; people with several accounts).
      // Forwarded to the IdP by Cognito managed login (v2). Same rule as grow-mobile's authorizeUrl.ts.
      window.location.href =
        `https://${domain}/oauth2/authorize?response_type=code` +
        `&client_id=${encodeURIComponent(clientId)}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&identity_provider=${provider}&scope=${encodeURIComponent(scope)}` +
        `&prompt=select_account` +
        `&code_challenge=${challenge}&code_challenge_method=S256`
    } catch (err) {
      // The challenge can throw (crypto.subtle is absent on a plain-http LAN host); without this the
      // loader stayed up with no way out.
      setBusy(false)
      setError(err instanceof Error ? err.message : t.somethingWrong)
    }
  }
  const onGoogle = () => startFederated('Google')
  const onApple = () => startFederated('SignInWithApple')

  const alerts = (
    <>
      {error && <div className="alert alert-error py-2 text-sm"><span>{error}</span></div>}
      {info && !error && <div className="alert alert-info py-2 text-sm"><span>{info}</span></div>}
    </>
  )
  const backToEmail = (
    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setStep('email'); setError(null); setInfo(null) }}>
      {t.useDifferentEmail}
    </button>
  )
  // Back to the Sign-in Chooser — only in the browser (in the shell the email step is the root).
  const backToChooser = !inShell && (
    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setStep('chooser'); setError(null); setInfo(null) }}>
      {t.back}
    </button>
  )
  const cancel = isExternal && (
    <button type="button" className="btn btn-ghost btn-xs" onClick={() => finish('cancelled')}>{t.cancel}</button>
  )

  const form = (onSubmit: () => void, children: React.ReactNode) => (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit() }} className="space-y-3">{children}</form>
  )
  const submitLabel = (label: string) => (busy ? <span className="loading loading-spinner loading-sm" /> : label)

  // Waiting state: while the initial session check runs, while an already-signed-in session is being
  // bounced back to the caller, and during any auth round-trip (password/confirm/create) — show a clear
  // full-card loader instead of a frozen form.
  if (isLoading || account || busy) {
    return <AuthLoading title={t.signingIn} subtitle={t.signingInSubtitle} />
  }

  // Sign-in Chooser (grill 2026-09-05): three peer options; email opens as a second step. Browser only
  // (in the shell the default step is 'email' and social is handled by the app's native login).
  if (step === 'chooser') {
    return (
      <AuthLayout title={t.chooserTitle} subtitle={t.chooserSubtitle}>
        <GoogleSignInButton onClick={onGoogle} disabled={busy} label={t.continueWithGoogle} />
        <AppleSignInButton onClick={onApple} disabled={busy} label={t.continueWithApple} />
        <button type="button" className="btn btn-outline w-full gap-2" onClick={() => { setStep('email'); setError(null); setInfo(null) }}>
          {t.continueWithEmail}
        </button>
        {alerts}
        {cancel}
      </AuthLayout>
    )
  }

  if (step === 'email') {
    return (
      <AuthLayout title={t.emailTitle} subtitle={t.emailSubtitle}>
        {form(onEmail, (
          <>
            <input className="input input-bordered w-full" type="email" placeholder={t.email} value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" autoFocus />
            {alerts}
            <button className="btn btn-primary w-full" disabled={busy}>{submitLabel(t.continue)}</button>
          </>
        ))}
        {backToChooser}
        {cancel}
      </AuthLayout>
    )
  }

  if (step === 'password') {
    return (
      <AuthLayout title={t.welcomeBack} subtitle={email}>
        {form(onPassword, (
          <>
            <input className="input input-bordered w-full" type="password" placeholder={t.password} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" autoFocus />
            {alerts}
            <button className="btn btn-primary w-full" disabled={busy}>{submitLabel(t.signIn)}</button>
          </>
        ))}
        <div className="flex justify-between">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setStep('forgot'); setError(null); setInfo(null) }}>{t.forgotPassword}</button>
          {backToEmail}
        </div>
        {cancel}
      </AuthLayout>
    )
  }

  if (step === 'create') {
    return (
      <AuthLayout title={t.createTitle} subtitle={email}>
        {form(onCreate, (
          <>
            <input className="input input-bordered w-full" placeholder={t.name} value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" autoFocus />
            <input className="input input-bordered w-full" type="password" placeholder={t.password} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" minLength={8} />
            {alerts}
            <button className="btn btn-primary w-full" disabled={busy}>{submitLabel(t.createAccount)}</button>
          </>
        ))}
        {backToEmail}
        {cancel}
      </AuthLayout>
    )
  }

  if (step === 'confirm') {
    return (
      <AuthLayout title={t.confirmTitle} subtitle={email}>
        {form(onConfirm, (
          <>
            <input className="input input-bordered w-full" placeholder={t.code6} value={code} onChange={(e) => setCode(e.target.value)} required inputMode="numeric" autoFocus />
            {!password && (
              <input className="input input-bordered w-full" type="password" placeholder={t.password} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
            )}
            {alerts}
            <button className="btn btn-primary w-full" disabled={busy}>{submitLabel(t.confirmCta)}</button>
          </>
        ))}
        <div className="flex justify-between">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onResend} disabled={busy}>{t.resendCode}</button>
          {backToEmail}
        </div>
        {cancel}
      </AuthLayout>
    )
  }

  if (step === 'google') {
    return (
      <AuthLayout title={t.googleTitle} subtitle={`${email} ${t.isGoogleAccount}`}>
        {!inShell && <GoogleSignInButton onClick={onGoogle} disabled={busy} label={t.continueWithGoogle} />}
        {alerts}
        {backToEmail}
        {cancel}
      </AuthLayout>
    )
  }

  if (step === 'apple') {
    return (
      <AuthLayout title={t.appleTitle} subtitle={`${email} ${t.isAppleAccount}`}>
        {!inShell && <AppleSignInButton onClick={onApple} disabled={busy} label={t.continueWithApple} />}
        {alerts}
        {backToEmail}
        {cancel}
      </AuthLayout>
    )
  }

  if (step === 'forgot') {
    return (
      <AuthLayout title={t.resetTitle} subtitle={t.resetSubtitle}>
        {form(onForgot, (
          <>
            <input className="input input-bordered w-full" type="email" placeholder={t.email} value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" autoFocus />
            {alerts}
            <button className="btn btn-primary w-full" disabled={busy}>{submitLabel(t.sendResetCode)}</button>
          </>
        ))}
        {backToEmail}
      </AuthLayout>
    )
  }

  // forgotConfirm
  return (
    <AuthLayout title={t.setNewTitle} subtitle={email}>
      {form(onForgotConfirm, (
        <>
          <input className="input input-bordered w-full" placeholder={t.resetCode} value={code} onChange={(e) => setCode(e.target.value)} required inputMode="numeric" autoFocus />
          <input className="input input-bordered w-full" type="password" placeholder={t.newPassword} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" minLength={8} />
          {alerts}
          <button className="btn btn-primary w-full" disabled={busy}>{submitLabel(t.setNewPassword)}</button>
        </>
      ))}
      {/* #490: `cancel` must be offered here too. Inside the native shell the email step is the root
          (backToChooser is suppressed), so without this a person who taps "forgot password" has NO
          in-page exit at all. */}
      {backToEmail}
      {cancel}
    </AuthLayout>
  )
}

export { RETURN_TO_KEY }
